import { toast } from '@tempo-io/sui-sonner';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

export interface JumpRegistrant {
  probe: (itemId: number) => boolean;
  scroll: (itemId: number, onComplete: (success: boolean) => void) => void;
}

interface JumpToItemOptions {
  retryUntilFound?: boolean;
}

interface JumpToItemContextValue {
  highlightedItemId: number | null;
  hasJumpHandler: boolean;
  registerJumpHandler: (registrant: JumpRegistrant) => () => void;
  jumpToItem: (itemId: number, options?: JumpToItemOptions) => void;
  isJumpInFlight: () => boolean;
}

const JumpToItemContext = createContext<JumpToItemContextValue | null>(null);

const HIGHLIGHT_DURATION_MS = 2000;
const JUMP_RETRY_INTERVAL_MS = 120;
const JUMP_MAX_RETRIES = 25;

export const JumpToItemProvider = ({ children }: { children: ReactNode }) => {
  const registry = useRef<Set<JumpRegistrant>>(new Set());
  const [hasJumpHandler, setHasJumpHandler] = useState(false);
  const [highlightedItemId, setHighlightedItemId] = useState<number | null>(null);
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const jumpInFlightRef = useRef(false);
  const jumpTokenRef = useRef(0);
  const { t } = useTranslation();

  useEffect(
    () => () => {
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    },
    [],
  );

  const registerJumpHandler = useCallback((registrant: JumpRegistrant) => {
    registry.current.add(registrant);
    setHasJumpHandler(true);
    return () => {
      registry.current.delete(registrant);
      if (registry.current.size === 0) setHasJumpHandler(false);
    };
  }, []);

  const notVisibleToast = useCallback(() => {
    toast.info(
      t(($) => $.toast.jumpToItem.notVisible.title),
      {
        description: t(($) => $.toast.jumpToItem.notVisible.description),
      },
    );
  }, [t]);

  const jumpToItem = useCallback(
    (itemId: number, options?: JumpToItemOptions) => {
      const token = ++jumpTokenRef.current;
      const settle = () => {
        if (jumpTokenRef.current === token) jumpInFlightRef.current = false;
      };

      const tryJump = (): boolean => {
        for (const r of registry.current) {
          if (r.probe(itemId)) {
            r.scroll(itemId, (success) => {
              settle();
              if (!success) {
                notVisibleToast();
                return;
              }
              if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
              setHighlightedItemId(itemId);
              highlightTimerRef.current = setTimeout(() => {
                setHighlightedItemId(null);
                highlightTimerRef.current = null;
              }, HIGHLIGHT_DURATION_MS);
            });
            return true;
          }
        }
        return false;
      };

      const run = (remaining: number) => {
        if (tryJump()) return;
        if (remaining > 0) {
          retryTimerRef.current = setTimeout(() => run(remaining - 1), JUMP_RETRY_INTERVAL_MS);
          return;
        }
        settle();
        notVisibleToast();
      };

      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      jumpInFlightRef.current = true;
      run(options?.retryUntilFound ? JUMP_MAX_RETRIES : 0);
    },
    [notVisibleToast],
  );

  const isJumpInFlight = useCallback(() => jumpInFlightRef.current, []);

  const value = useMemo(
    () => ({ highlightedItemId, hasJumpHandler, registerJumpHandler, jumpToItem, isJumpInFlight }),
    [highlightedItemId, hasJumpHandler, registerJumpHandler, jumpToItem, isJumpInFlight],
  );

  return <JumpToItemContext.Provider value={value}>{children}</JumpToItemContext.Provider>;
};

export const useJumpToItem = () => useContext(JumpToItemContext);

export const useJumpToItemHandler = () => {
  const ctx = useContext(JumpToItemContext);
  return ctx?.hasJumpHandler ? ctx.jumpToItem : undefined;
};
